import type {
  ApplyExtractionApiResponse,
  ExtractionDiffApiResponse,
  OcrFailedReason,
  SourceFileApiResponse,
} from '../../features/extraction-sources/extraction-sources.models';

/**
 * 抽出ソースファイルのMockデータ
 * projectId をキーとし、各プロジェクトのソースファイル一覧を保持
 * 1プロジェクトに対し抽出ソースは最大1ファイル（openapi 仕様: 既存ソースは削除の上新規作成）
 *
 * Issue #475 Phase 3 / Issue #550: `ocrFailedReason` は `ocrStatus='failed'` のときだけ
 * 値を持つ。project-007〜009 / project-015 に 4 状態（usage_limit_exceeded /
 * ai_server_error / null = 理由不明 / conversion_error）それぞれの失敗ソースを
 * 用意している。
 *
 * 注意: 総括表（SourceFile）側の失敗理由そのものは **まだ一覧に出していない**。
 * 総括表の失敗はバナーで伝えており、その文言は Project 側の `extractionFailedReason`
 * （projects.mock）で出し分ける（Issue #475 Phase 5.5）。
 * Project の extractionStatus との対応は実 API と揃えている:
 *   - 402（利用上限）由来:      Project も failed + usage_limit_exceeded（Phase 5.5）
 *   - それ以外（webhook 経由）: Project も failed + ai_server_error / null（理由不明）
 *                              / conversion_error（変換失敗。Issue #550）
 * この対応は mocks/mock-data/ocr-failed-reason.spec.ts で固定している。
 */
export const MOCK_SOURCE_FILES: Record<string, SourceFileApiResponse[]> = {
  'project-001': [
    {
      publicId: 'source-file-001',
      fileName: '人件費総括表_2025年度.xlsx',
      fileHash:
        'src-a1b2c3d4e5f6a1b2c3d4e5f6a1b2c3d4e5f6a1b2c3d4e5f6a1b2c3d4e5f6a1b2',
      uploadStatus: 'uploaded',
      ocrStatus: 'completed',
      ocrFailedReason: null,
      createdAt: '2025-01-09T15:00:00.000Z',
      updatedAt: '2025-01-09T15:30:00.000Z',
    },
  ],
  'project-002': [
    {
      publicId: 'source-file-002',
      fileName: '人件費総括表_2024年度.xlsx',
      fileHash:
        'src-b2c3d4e5f6g7b2c3d4e5f6g7b2c3d4e5f6g7b2c3d4e5f6g7b2c3d4e5f6g7b2c3',
      uploadStatus: 'uploaded',
      ocrStatus: 'completed',
      ocrFailedReason: null,
      createdAt: '2025-01-10T15:00:00.000Z',
      updatedAt: '2025-01-10T15:30:00.000Z',
    },
  ],
  'project-003': [
    {
      publicId: 'source-file-003',
      fileName: '人件費総括表_アルバイト_2025年度.xlsx',
      fileHash:
        'src-c3d4e5f6g7h8c3d4e5f6g7h8c3d4e5f6g7h8c3d4e5f6g7h8c3d4e5f6g7h8c3d4',
      uploadStatus: 'uploaded',
      ocrStatus: 'completed',
      ocrFailedReason: null,
      createdAt: '2025-01-08T15:00:00.000Z',
      updatedAt: '2025-01-08T15:30:00.000Z',
    },
  ],
  'project-004': [], // ExtractionSource なし
  'project-005': [
    {
      publicId: 'source-file-005',
      fileName: '人件費総括表_君津_2024年度.xlsx',
      fileHash:
        'src-e5f6g7h8i9j0e5f6g7h8i9j0e5f6g7h8i9j0e5f6g7h8i9j0e5f6g7h8i9j0e5f6',
      uploadStatus: 'uploading',
      ocrStatus: 'idle',
      ocrFailedReason: null,
      createdAt: '2025-01-14T07:00:00.000Z',
      updatedAt: '2025-01-14T07:00:00.000Z',
    },
  ],
  'project-006': [], // ExtractionSource なし
  // Issue #475 Phase 3: OCR 失敗理由の 3 状態ぶんのデータ。
  // 「AI 利用上限に到達」= 追加付与まで解消しない恒久エラー
  // （Phase 5.5 以降は Project も failed + usage_limit_exceeded になる）
  'project-007': [
    {
      publicId: 'source-file-007',
      fileName: '人件費総括表_上限到達_2025年度.xlsx',
      fileHash:
        'src-f6g7h8i9j0k1f6g7h8i9j0k1f6g7h8i9j0k1f6g7h8i9j0k1f6g7h8i9j0k1f6g7',
      uploadStatus: 'uploaded',
      ocrStatus: 'failed',
      ocrFailedReason: 'usage_limit_exceeded',
      createdAt: '2026-08-20T02:00:00.000Z',
      updatedAt: '2026-08-20T02:05:00.000Z',
    },
  ],
  // 「AI サーバー起因の失敗」（Project も ExtractionFailed になる）
  'project-008': [
    {
      publicId: 'source-file-008',
      fileName: '人件費総括表_AIエラー_2025年度.xlsx',
      fileHash:
        'src-g7h8i9j0k1l2g7h8i9j0k1l2g7h8i9j0k1l2g7h8i9j0k1l2g7h8i9j0k1l2g7h8',
      uploadStatus: 'uploaded',
      ocrStatus: 'failed',
      ocrFailedReason: 'ai_server_error',
      createdAt: '2026-08-20T03:00:00.000Z',
      updatedAt: '2026-08-20T03:05:00.000Z',
    },
  ],
  // 「理由不明」= Phase 3 以前に失敗した既存行（遡って理由を埋められない。
  // webhook 経由の失敗なので Project も ExtractionFailed）
  'project-009': [
    {
      publicId: 'source-file-009',
      fileName: '人件費総括表_理由不明_2024年度.xlsx',
      fileHash:
        'src-h8i9j0k1l2m3h8i9j0k1l2m3h8i9j0k1l2m3h8i9j0k1l2m3h8i9j0k1l2m3h8i9',
      uploadStatus: 'uploaded',
      ocrStatus: 'failed',
      ocrFailedReason: null,
      createdAt: '2026-07-01T05:00:00.000Z',
      updatedAt: '2026-07-01T05:05:00.000Z',
    },
  ],
  // Issue #550: 「変換工程の失敗」（ConvertToPreviewFailed webhook 由来）。
  // 総括表は preview 変換に失敗すると抽出自体が始まらないため、修正前は
  // OcrProcessing のまま固まっていた。修正後は conversion_error で終端する
  // （Project も failed + conversion_error）
  'project-015': [
    {
      publicId: 'source-file-015',
      fileName: '人件費総括表_変換エラー_2025年度.xlsx',
      fileHash:
        'src-i9j0k1l2m3n4i9j0k1l2m3n4i9j0k1l2m3n4i9j0k1l2m3n4i9j0k1l2m3n4i9j0',
      uploadStatus: 'uploaded',
      ocrStatus: 'failed',
      ocrFailedReason: 'conversion_error',
      createdAt: '2026-09-01T02:00:00.000Z',
      updatedAt: '2026-09-01T02:05:00.000Z',
    },
  ],
};

/**
 * projectId からソースファイル一覧を取得
 * エントリが存在しない projectId の場合は空配列を返す
 */
export function findSourceFilesByProjectId(
  projectId: string,
): SourceFileApiResponse[] {
  return MOCK_SOURCE_FILES[projectId] ?? [];
}

/**
 * プロジェクトにソースファイルを追加
 * 既存ファイルは削除の上、新規作成（openapi 仕様準拠）
 */
export function addSourceFile(
  projectId: string,
  file: SourceFileApiResponse,
): void {
  // 既存ファイルを削除して新規作成（1プロジェクト1ファイルの仕様）
  MOCK_SOURCE_FILES[projectId] = [file];
}

/**
 * プロジェクトのソースファイルを「OCR 失敗」に落とす（Issue #475 Phase 5.5）
 *
 * 解析失敗のシミュレーション（projects.handler のポーリング終端）から呼ぶ。
 * Project だけを failed にすると「Project は失敗 / ソースは OCR 未実行」という
 * 実 API では作れない組み合わせが wire に載るため、ソース側も揃える。
 *
 * @param projectId 対象プロジェクトID
 * @param reason OCR 失敗理由（null は理由不明）
 */
export function markSourceFileOcrFailed(
  projectId: string,
  reason: OcrFailedReason | null,
): void {
  if (!(projectId in MOCK_SOURCE_FILES)) return;

  MOCK_SOURCE_FILES[projectId] = MOCK_SOURCE_FILES[projectId].map((file) => ({
    ...file,
    uploadStatus: 'uploaded',
    ocrStatus: 'failed',
    ocrFailedReason: reason,
    updatedAt: new Date().toISOString(),
  }));
}

/**
 * プロジェクトが抽出ソースを持つか算出
 * MOCK_SOURCE_FILES の内容から動的に判定する
 */
export function hasExtractionSource(projectId: string): boolean {
  return findSourceFilesByProjectId(projectId).length > 0;
}

// ========================================
// 差分（diff）モックデータ
// ========================================

/**
 * プロジェクト別の差分モックデータ
 * project-001: 追加2件・更新1件・削除1件・警告1件
 * project-002: 更新3件・その他0件
 * その他: 変更なし
 */
export const MOCK_EXTRACTION_DIFF: Record<string, ExtractionDiffApiResponse> = {
  'project-001': {
    added: [
      {
        employeeId: 'EMP-0101',
        employeeName: '田中 太郎',
        reviewType: 'employment',
        targetMonth: '2025-04',
      },
      {
        employeeId: 'EMP-0102',
        employeeName: '鈴木 花子',
        reviewType: 'journal_entry',
        targetMonth: '2025-04',
      },
    ],
    updated: [
      {
        publicId: 'case-001-upd',
        employeeId: 'EMP-0050',
        reviewType: 'employment',
        targetMonth: '2025-04',
        before: { employeeName: '佐藤 旧名' },
        after: { employeeName: '佐藤 新名' },
      },
    ],
    removed: [
      {
        publicId: 'case-001-rem',
        employeeId: 'EMP-0099',
        employeeName: '廃止 社員',
        reviewType: 'employment',
        targetMonth: '2025-04',
      },
    ],
    warnings: [
      {
        type: 'DuplicateEmployeeIdMerged',
        employeeId: 'EMP-0050',
        sourceCount: 2,
      },
    ],
  },
  'project-002': {
    added: [],
    updated: [
      {
        publicId: 'case-002-upd-1',
        employeeId: 'EMP-0201',
        reviewType: 'employment',
        before: { employeeName: '旧姓 一郎' },
        after: { employeeName: '新姓 一郎' },
      },
      {
        publicId: 'case-002-upd-2',
        employeeId: 'EMP-0202',
        reviewType: 'journal_entry',
        before: { employeeName: '山田 旧' },
        after: { employeeName: '山田 新' },
      },
      {
        publicId: 'case-002-upd-3',
        employeeId: 'EMP-0203',
        reviewType: 'employment',
        before: { employeeName: '中村 旧' },
        after: { employeeName: '中村 新' },
      },
    ],
    removed: [],
    warnings: [],
  },
};

/**
 * 差分モックデータのデフォルト（変更なし）
 */
const EMPTY_DIFF: ExtractionDiffApiResponse = {
  added: [],
  updated: [],
  removed: [],
  warnings: [],
};

/**
 * projectId から差分モックデータを取得
 */
export function findExtractionDiffByProjectId(
  projectId: string,
): ExtractionDiffApiResponse {
  return MOCK_EXTRACTION_DIFF[projectId] ?? EMPTY_DIFF;
}

// ========================================
// 適用（apply）モックデータ
// ========================================

/**
 * プロジェクト別の適用結果モックデータ
 */
export const MOCK_APPLY_RESULT: Record<string, ApplyExtractionApiResponse> = {
  'project-001': {
    applied: true,
    addedCaseIds: ['case-new-0101', 'case-new-0102'],
    updatedCaseIds: ['case-001-upd'],
    removedCaseIds: ['case-001-rem'],
    warnings: [
      {
        type: 'DuplicateEmployeeIdMerged',
        employeeId: 'EMP-0050',
        sourceCount: 2,
      },
    ],
  },
  'project-002': {
    applied: true,
    addedCaseIds: [],
    updatedCaseIds: ['case-002-upd-1', 'case-002-upd-2', 'case-002-upd-3'],
    removedCaseIds: [],
    warnings: [],
  },
};

/**
 * 適用結果モックデータのデフォルト
 */
const EMPTY_APPLY_RESULT: ApplyExtractionApiResponse = {
  applied: true,
  addedCaseIds: [],
  updatedCaseIds: [],
  removedCaseIds: [],
  warnings: [],
};

/**
 * projectId から適用結果モックデータを取得
 */
export function findApplyResultByProjectId(
  projectId: string,
): ApplyExtractionApiResponse {
  return MOCK_APPLY_RESULT[projectId] ?? EMPTY_APPLY_RESULT;
}

// ========================================
// 実行ラン public ID モックデータ
// ========================================

/**
 * プロジェクト別の実行ラン public ID マップ
 * handleGetProject で callCount >= 3 になったときに latestExtractionRunPublicId として返す値と一致させること
 */
export const MOCK_EXECUTION_RUN_PUBLIC_IDS: Record<string, string> = {
  'project-001': 'run_mock123',
  'project-002': 'run_mock456',
};
